import { Box, Typography, Chip, Button, IconButton, Avatar } from '@mui/joy';
import CreditCardRoundedIcon   from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon     from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon  from '@mui/icons-material/PointOfSaleRounded';
import ReceiptLongRoundedIcon  from '@mui/icons-material/ReceiptLongRounded';
import BlockRoundedIcon        from '@mui/icons-material/BlockRounded';

// Виды оплаты — цвета/иконки как в «Истории чеков» рабочего аккаунта
const MONEY_META = {
    transfer: { label: 'Перевод',  color: '#818cf8', bg: 'rgba(129,140,248,0.10)', icon: <CreditCardRoundedIcon  sx={{ fontSize: 14 }} /> },
    cash:     { label: 'Наличные', color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   icon: <PaymentsRoundedIcon    sx={{ fontSize: 14 }} /> },
    terminal: { label: 'Терминал', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} /> },
};

const fmtTime  = (iso) => (iso ? new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—');
const fmtMoney = (v)   => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

/**
 * Карточка одного логического чека — один в один с «Историей чеков» рабочего аккаунта.
 *
 * props:
 *  - group:      массив Order'ов одного чека (общий receipt_uid). Обязателен.
 *  - onConfirm:  (order) => void  — если передан, у неподтверждённого чека будет кнопка «Подтвердить».
 *  - onDelete:   (order) => void  — если передан, будет кнопка удаления (иконка).
 *  - busy:       boolean — блокирует кнопки на время запроса.
 *
 * Кнопки показываются только если переданы соответствующие обработчики.
 * Без обработчиков карточка выглядит так же, но только для просмотра.
 */
export default function CheckCard({ group, onConfirm, onDelete, busy = false }) {
    if (!group || group.length === 0) return null;

    const head          = group[0];
    const groupTotal    = group.reduce((s, o) => s + parseFloat(o.value || 0), 0);
    const allItems      = group.flatMap(o => o.items || []);
    const groupComment  = group.map(o => o.comment).find(c => c && c.trim()) || '';
    const isPending     = group.some(o => o.confirmation_status === 'pending');
    const photographerAt = head.receipt_datetime || head.date_create;
    const adminAt        = head.date_create;
    const hasTwoTimes    = head.receipt_datetime
        && Math.abs(new Date(head.receipt_datetime).getTime() - new Date(head.date_create).getTime()) > 60_000;

    const confirmDelete = () => {
        if (window.confirm('Удалить чек? Он уйдёт из кассы, выручка пересчитается.')) {
            group.forEach(o => onDelete(o));
        }
    };

    return (
        <Box sx={{
            bgcolor: 'var(--joy-palette-background-level1)',
            border: '1px solid var(--joy-palette-divider)',
            borderRadius: 'var(--joy-radius-lg)',
            borderLeft: groupComment ? '3px solid #f59e0b' : '1px solid var(--joy-palette-divider)',
            p: 1.5,
        }}>
            {/* Верх: аватар + имя + разбивка по оплатам + общий итог */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar src={head.photographer_avatar_url} size="sm" sx={{ bgcolor: 'var(--joy-palette-primary-500)' }}>
                    {(head.photographer_name || '').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        {head.photographer_name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.4 }}>
                        {group.map(o => {
                            const meta = MONEY_META[o.money_type] || MONEY_META.transfer;
                            return (
                                <Chip key={o.id} size="sm" startDecorator={meta.icon}
                                    sx={{ bgcolor: meta.bg, color: meta.color, fontSize: 10, fontWeight: 700 }}>
                                    {meta.label} · {fmtMoney(o.value)} ₽
                                </Chip>
                            );
                        })}
                    </Box>
                </Box>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', whiteSpace: 'nowrap' }}>
                    {fmtMoney(groupTotal)} ₽
                </Typography>
            </Box>

            {/* Статус + кнопки (на весь чек) */}
            {isPending ? (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: 10, fontWeight: 700 }}>
                        Не подтверждён
                    </Chip>
                    {onConfirm && (
                        <Button
                            size="sm"
                            startDecorator={<ReceiptLongRoundedIcon sx={{ fontSize: 15 }} />}
                            onClick={() => group.forEach(o => o.confirmation_status === 'pending' && onConfirm(o))}
                            disabled={busy}
                            sx={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: '#fff', fontWeight: 700, fontSize: 11 }}
                        >
                            Подтвердить
                        </Button>
                    )}
                    {onDelete && (
                        <IconButton size="sm" variant="outlined" color="danger" onClick={confirmDelete} disabled={busy}>
                            <BlockRoundedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    )}
                </Box>
            ) : (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip size="sm" variant="soft" color="success" sx={{ fontSize: 10, fontWeight: 700 }}>
                        Подтверждён
                    </Chip>
                    {onDelete && (
                        <IconButton size="sm" variant="outlined" color="danger" onClick={confirmDelete} disabled={busy}>
                            <BlockRoundedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    )}
                </Box>
            )}

            {/* Комментарий */}
            {groupComment && (
                <Box sx={{ mt: 1, px: 1.2, py: 0.8, bgcolor: 'var(--joy-palette-background-level2)', borderRadius: 'var(--joy-radius-sm)', borderLeft: '3px solid #f59e0b' }}>
                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {groupComment}
                    </Typography>
                </Box>
            )}

            {/* Позиции — объединённые по всему чеку */}
            {allItems.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {allItems.map((it, i) => (
                        <Chip key={i} size="sm" variant="soft" sx={{ fontSize: 10, fontWeight: 600, bgcolor: 'var(--joy-palette-background-level2)', color: 'var(--joy-palette-text-secondary)' }}>
                            {it.product_name_snapshot}{it.quantity > 1 ? ` × ${it.quantity}` : ''}
                        </Chip>
                    ))}
                </Box>
            )}

            {/* Кто оформил */}
            {head.processed_by_name && (
                <Typography sx={{ mt: 0.8, fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                    Оформил:{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: '#22c55e' }}>
                        {head.processed_by_name}
                    </Box>
                </Typography>
            )}

            {/* Времена: оформление фотографом → подтверждение админом */}
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.2, fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                {hasTwoTimes ? (
                    <>
                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                            <Box component="span" sx={{ fontWeight: 700, color: '#f59e0b' }}>Фотограф</Box>
                            {' · '}{fmtTime(photographerAt)}
                        </Typography>
                        <Box sx={{ color: 'var(--joy-palette-text-tertiary)', fontSize: 10 }}>→</Box>
                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                            <Box component="span" sx={{ fontWeight: 700, color: '#22c55e' }}>Админ</Box>
                            {' · '}{fmtTime(adminAt)}
                        </Typography>
                    </>
                ) : isPending ? (
                    <Typography sx={{ fontSize: 10, color: '#f59e0b', fontWeight: 700 }}>
                        Ожидает подтверждения
                    </Typography>
                ) : (
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                        Оформлен в {fmtTime(adminAt)}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}